<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <h1>{{ $t('subscriptions.views.SubscriptionBilling.invoiceHistory') }}</h1>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <SearchTextField v-model="searchQuery"/>
      </v-col>
      <v-col class="d-flex align-center">
        <h3 class="mr-3 ml-auto">
          {{ $t('subscriptions.views.SubscriptionBilling.yearFilter') }}
        </h3>
        <AppSelect v-model="selectedYear"
                   class="SubscriptionBilling-yearFilter bg-white"
                   :items="selectableYears"
                   no-label
        />
      </v-col>
    </v-row>

    <template v-if="selectedInvoices.length > 0">
      <v-row>
        <v-col>
          <div class="SubscriptionBilling-multiSelect-bar">
            <h4 v-t="{
              path: 'subscriptions.views.SubscriptionBilling.selectedInvoices',
              plural: selectedInvoices.length,
              args: { count: selectedInvoices.length },
            }" />
            <div class="app-link ml-3"
                 @click="selectedInvoices = []"
            >
              {{ $t('subscriptions.views.SubscriptionBilling.unSelect') }}
            </div>
            <AppDownloadFile :link="invoicesZipLink">
              <template #default="{submitForm}">
                <MultiSelectBarButton class="ml-3"
                                      @click="submitForm"
                >
                  {{ $t('common.download') }}
                </MultiSelectBarButton>
              </template>
            </AppDownloadFile>
          </div>
        </v-col>
      </v-row>
    </template>

    <template v-if="subscriptionInvoicesPending && !subscriptionInvoices">
      <v-row>
        <v-col class="d-flex justify-center">
          <AppDataTableSkeletonLoader class="w-100"/>
        </v-col>
      </v-row>
    </template>

    <v-row v-if="subscriptionInvoices && !subscriptionInvoicesPending">
      <v-col>
        <v-data-table v-model="selectedInvoices"
                      :headers="headers"
                      :row-props="invoiceRowProps"
                      :items="filteredInvoices"
                      :page="currentPage"
                      :items-per-page="itemsPerPage"
                      class="SubscriptionBilling-table"
                      show-select
        >
          <template #item.reference="{item}">
            {{ item.ref }}
          </template>
          <template #item.invoiceDate="{item}">
            {{ ISOToShortenedDate(item.invoiceDate) }}
          </template>
          <template #item.dueDate="{item}">
            {{ ISOToShortenedDate(item.dueDate) }}
          </template>
          <template #item.status="{item}">
            <div class="d-flex items-centers" :class="{ 'text-error': item.status === 'sent' && dayjs().isAfter(item.dueDate) }">
              {{ getStatusTranslation(item) }}
              <router-link v-if="item.status === 'sent' && dayjs().isBefore(item.dueDate)"
                           :to="{ name: PAYMENT_ROUTE_NAME, params: { locale: $i18n.locale, token: item.token } }"
              >
                
                <app-material-icon name="credit-card" type="fa" size="xs" class="SubscriptionBilling-creditCardIcon ml-1 clickable" />
              </router-link>
              <router-link v-if="item.status === 'sent' && dayjs().isAfter(item.dueDate)"
                           :to="{ name: PAYMENT_ROUTE_NAME, params: { locale: $i18n.locale, token: item.token } }"
              >
                <app-material-icon name="credit-card" type="fa" size="xs" class="SubscriptionBilling-creditCardIcon ml-1 clickable" />
              </router-link>
            </div>
          </template>
          <template #item.amountGross="{item}">
            {{ item.amountGross.toString().replace('.', ',') }} €
          </template>
          <template #item.dates="{item}">
            {{ $t('subscriptions.views.SubscriptionBilling.period', { startDate: ISOToDDMMYYYY(item.fromDate), endDate: ISOToDDMMYYYY(item.toDate) }) }}
          </template>
          <template #item.options="{item}">
            <AppDownloadFile :link="getInvoiceLink(item)">
              <template #default="{submitForm}">
                <div class="app-link" @click="submitForm">
                  <app-material-icon name="file-pdf" type="fa" size="xs" class="text-error" />
                  {{ $t('common.download') }}
                </div>
              </template>
            </AppDownloadFile>
          </template>

          <template #bottom>
            <div class="d-flex justify-end mt-4">
              <AppPagination v-model:current-page="currentPage"
                             v-model:page-size="itemsPerPage"
                             :pageSizes="ITEMS_PER_PAGE"
                             :total="filteredInvoices.length"
              />
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import dayjs from 'dayjs'
import { mapActions, mapGetters, mapState, useStore } from 'vuex'

import AppDataTableSkeletonLoader from '@/common/AppDataTableSkeletonLoader.vue'
import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppSelect from '@/common/AppSelect.vue'
import MultiSelectBarButton from '@/common/buttons/MultiSelectBarButton.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import { usePagination } from '@/common/pagination/usePagination'
import SearchTextField from '@/common/SearchTextField.vue'
import { ISOToShortenedDate, ISOToDDMMYYYY } from '@/common/utils/dates'
import { toast } from '@/common/utils/toast'
import { PAYMENT_ROUTE_NAME } from '@/router'
import {
  DOWNLOAD_INVOICES,
  GET_SUBSCRIPTION_INVOICES,
} from '@/store/modules/subscription/action_types'

import { SUBSCRIPTION_BILLING_ITEMS_PER_PAGE } from '../constants.js'

const ITEMS_PER_PAGE = [50, 100, 250]

export default {
  name: 'SubscriptionBilling',
  components: {
    AppPagination,
    AppDataTableSkeletonLoader,
    AppDownloadFile,
    SearchTextField,
    AppSelect,
    MultiSelectBarButton,
  },
  setup: () => {
    const store = useStore()
    const { currentPage, itemsPerPage } = usePagination({
      currentUserId: store.getters['user/currentUserId'],
      userSettingsKey: SUBSCRIPTION_BILLING_ITEMS_PER_PAGE,
      pageSize: ITEMS_PER_PAGE[0],
    })

    return {
      currentPage,
      itemsPerPage,
    }
  },
  data () {
    return {
      searchQuery: '',
      ISOToShortenedDate,
      ISOToDDMMYYYY,
      dayjs,
      selectedInvoices: [],
      selectedYear: dayjs().year(),
      headers: [
        { value: 'reference', title: this.$t('subscriptions.views.SubscriptionBilling.headers.reference'), sortable: true },
        { value: 'invoiceDate', title: this.$t('subscriptions.views.SubscriptionBilling.headers.invoiceDate'), sortable: true },
        { value: 'dueDate', title: this.$t('subscriptions.views.SubscriptionBilling.headers.dueDate'), sortable: true },
        { value: 'status', title: this.$t('subscriptions.views.SubscriptionBilling.headers.status'), sortable: true },
        { value: 'amountGross', title: this.$t('subscriptions.views.SubscriptionBilling.headers.amountGross'), sortable: true },
        { value: 'dates', title: this.$t('subscriptions.views.SubscriptionBilling.headers.dates'), sortable: true },
        { value: 'options', title: this.$t('subscriptions.views.SubscriptionBilling.headers.options') },
      ],
      ITEMS_PER_PAGE,
      PAYMENT_ROUTE_NAME,
    }
  },
  computed: {
    ...mapState('subscription', ['subscriptionInvoices', 'subscriptionInvoicesPending']),
    ...mapGetters('user', ['currentUserId']),
    invoicesZipLink () {
      return `${import.meta.env.VUE_APP_API_URL}/subscription/download/invoices?invoicesIds=${this.selectedInvoices.map(invoice => invoice.id)}`
    },
    selectableYears () {
      const years = []
      for (let i = dayjs().year(); i >= 2020; i--) {
        years.push(i)
      }
      return years
    },
    filteredInvoices () {
      if (this.subscriptionInvoices) {
        const invoicesByYear = this.subscriptionInvoices.data.filter(invoice => dayjs(invoice.invoiceDate).year() === this.selectedYear)

        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase()
          return invoicesByYear.filter((invoice) => {
            const statusTranslation = this.getStatusTranslation(invoice).toLowerCase()
            return (
              invoice.ref.toLowerCase().includes(query) ||
              statusTranslation.includes(query)
            )
          })
        }
        return invoicesByYear
      }
      return []
    },
  },
  mounted () {
    this.prepareGetSubscriptionInvoices()
  },
  methods: {
    ...mapActions('subscription', [GET_SUBSCRIPTION_INVOICES, DOWNLOAD_INVOICES]),
    async prepareGetSubscriptionInvoices () {
      try {
        await this.GET_SUBSCRIPTION_INVOICES()
      } catch (error) {
        toast.error(this.$t('subscriptions.views.SubscriptionBilling.getSubscriptionInvoicesError'))
      }
    },
    getStatusTranslation (invoice) {
      const status = invoice.status
      const isBeforeDueDate = dayjs().isBefore(invoice.dueDate)
      const isAfterDueDate = dayjs().isAfter(invoice.dueDate)

      if (status === 'paid') {
        return this.$t('subscriptions.views.SubscriptionBilling.paymentStatus.paid')
      } else if (status === 'sent') {
        if (isBeforeDueDate) {
          return this.$t('subscriptions.views.SubscriptionBilling.paymentStatus.sent')
        } else if (isAfterDueDate) {
          return this.$t('subscriptions.views.SubscriptionBilling.paymentStatus.overdue')
        }
      }
      return ''
    },
    invoiceRowProps ({ item: invoice }) {
      const classes = []
      if (invoice.status === 'sent' && dayjs().isAfter(invoice.dueDate)) {
        classes.push('errorBorder')
      }

      if (this.selectedInvoices.includes(invoice.id)) {
        classes.push('selectedInvoice')
      }

      return { class: classes.join(' ') }
    },
    getInvoiceLink (item) {
      return `${import.meta.env.VUE_APP_API_URL}/subscription/download/invoice/${item.id}`
    },
  },
}
</script>

<style scoped lang="scss">
:deep(.SubscriptionBilling-yearFilter) {
  max-width: 150px;
}
:deep(.SubscriptionBilling-table) {
  background-color: transparent;

  table {
    border-collapse: separate;
    border-spacing: 0 5px;
  }
  th {
    color: rgb(var(--v-theme-accent)) !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    border-bottom: none !important;
  }
  td {
    color: rgb(var(--v-theme-accent)) !important;
    font-size: 14px !important;
    border-bottom: none !important;
    vertical-align: center;
    height: 52px !important;
    &:first-child {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
    &:last-child {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }
  tbody {
    tr {
      background-color: #fff;
      &.errorBorder {
        td {
          border-top: 1px solid rgb(var(--v-theme-error)) !important;
          border-bottom: 1px solid rgb(var(--v-theme-error)) !important;
          &:first-child {
            border-left: 1px solid rgb(var(--v-theme-error)) !important;
          }
          &:last-child {
            border-right: 1px solid rgb(var(--v-theme-error)) !important;
          }
        }
      }
      &.selectedInvoice {
        td {
          background-color: rgb(var(--v-theme-primary-lighten-4));
        }
      }
    }
  }
}
.SubscriptionBilling-multiSelect-bar {
  display: flex;
  align-items: center;
  border: 1px solid rgb(var(--v-theme-primary));
  border-radius: 8px;
  padding-left: 20px;
}

:deep(.theme--light.v-text-field > .v-input__control > .v-input__slot:before) {
  border: none;
}

a > svg {
  vertical-align: middle;
}
</style>
